"use client"

/**
 * Ask Leo What's new card shell — brand thinking chrome only.
 *
 * No cursor spotlight. Respects `thinkingAnimationStyle`: blobs (one-shot)
 * or pulse perimeter. Search-bar wash is out of scope here.
 *
 * The shell also owns whether the card's Leo star is animating. The star is a
 * cursor-aware hero whose breath and twinkles run from an animation frame loop
 * over SVG transforms, which cannot be composited — left on, one card in a shelf
 * kept the whole home page repainting at display rate forever. Hover is the
 * trigger because the pointer is nowhere near a card in a horizontal shelf until
 * the reader means it, and the star is far too small a target to ask them to hit.
 */

import * as React from "react"
import { useReducedMotion } from "motion/react"

import { useLeoAmbience } from "@/components/leo-ambience-context"
import { LeoPulseBorder } from "@/components/leo-pulse-border"
import { LeoThinkingBackdrop } from "@/components/leo-thinking-backdrop"
import { LeoIcon } from "@/components/ui/leo-icon"
import { cn } from "@/lib/utils"

const CardPresenceContext = React.createContext(false)

export function WhatsNewLeoAmbience({
  children,
  className,
  style,
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  const reduceMotion = useReducedMotion() ?? false
  const { prefs } = useLeoAmbience()
  const usePulse = prefs.thinkingAnimationStyle === "pulse"
  const [present, setPresent] = React.useState(false)

  return (
    <div
      className={cn(
        "relative h-full overflow-hidden rounded-xl border bg-card shadow-xs",
        className,
      )}
      style={style}
      onPointerEnter={() => setPresent(true)}
      onPointerLeave={() => setPresent(false)}
      onFocusCapture={() => setPresent(true)}
      onBlurCapture={() => setPresent(false)}
    >
      {!reduceMotion && prefs.thinkingBlob && !usePulse ? (
        <LeoThinkingBackdrop
          thinking
          intensity="high"
          opacity={prefs.thinkingBlobOpacity}
          animate={prefs.blobAnimations}
          playOnce
          animationSpeed={prefs.blobAnimationSpeed}
        />
      ) : null}
      {!reduceMotion && prefs.thinkingBlob && usePulse ? (
        <LeoPulseBorder
          active
          strength={prefs.thinkingBlobOpacity}
          animate={prefs.blobAnimations}
          animationSpeed={prefs.blobAnimationSpeed}
        />
      ) : null}
      <CardPresenceContext.Provider value={present}>
        <div className="relative z-[1] h-full min-w-0">{children}</div>
      </CardPresenceContext.Provider>
    </div>
  )
}

/**
 * The card's Leo mark. Alive while the reader is on the card, still otherwise,
 * so the shelf costs nothing to leave on screen.
 */
export function WhatsNewLeoMark() {
  const present = React.useContext(CardPresenceContext)
  return <LeoIcon variant="interactive" size="xl" motionLive={present} />
}
